import { Button, Caption1, mergeClasses, Tooltip } from '@fluentui/react-components'
import { ArrowMaximizeRegular, ArrowMinimizeRegular, Attach20Regular } from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import useStyles from './ItemsContextPill.styles'

interface ItemsContextPillProps {
  activeContextCount: number
  isExpanded: boolean
  onExpandedChange: (next: boolean) => void
  // When true, only the paperclip attach control is shown (iMatch loading swap).
  hideExtended?: boolean
}

/**
 * Context row items control with a deliberate click split:
 * - paperclip always → `/files`
 * - expand body → expand/collapse only (never navigate)
 *
 * When there are active items (and not iMatch-hidden), a BrandBackground2 pill
 * groups paperclip → expand → count as one control. Empty count = paperclip only.
 */
export const ItemsContextPill = ({
  activeContextCount,
  isExpanded,
  onExpandedChange,
  hideExtended = false,
}: ItemsContextPillProps) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const showExtended = activeContextCount > 0 && !hideExtended
  const toggleAriaLabel = isExpanded
    ? t('chatInput.collapseContexts')
    : t('chatInput.expandContexts')
  const itemsLabel = t('contextBar.itemsCount', { count: activeContextCount })
  const ExpandIcon = isExpanded ? ArrowMinimizeRegular : ArrowMaximizeRegular

  return (
    <div
      className={mergeClasses(classes.root, showExtended && classes.pill)}
      data-testid="items-context-pill"
      data-extended={showExtended ? 'true' : 'false'}>
      <Tooltip content={t('chatInput.contextLink')} relationship="label" appearance="inverted">
        <Button
          className={classes.attachButton}
          appearance="primary"
          size="medium"
          shape="circular"
          icon={<Attach20Regular />}
          aria-label={t('chatInput.contextLink')}
          onClick={() => navigate('/files')}
        />
      </Tooltip>
      {showExtended && (
        <Tooltip content={toggleAriaLabel} relationship="label" appearance="inverted">
          <Button
            className={classes.expandButton}
            appearance="subtle"
            size="small"
            shape="circular"
            iconPosition="after"
            icon={<ExpandIcon className={classes.expandIcon} />}
            onClick={() => onExpandedChange(!isExpanded)}
            aria-label={toggleAriaLabel}>
            <Caption1 className={classes.itemsText}>{itemsLabel}</Caption1>
          </Button>
        </Tooltip>
      )}
    </div>
  )
}
